import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Zap, ArrowLeft, Loader2, Gift } from 'lucide-react';

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(searchParams.get('mode') !== 'register');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    promoting: '',
    referral_code: ''
  });
  
  const { login, register } = useAuth();
  const navigate = useNavigate();

  // Get referral code from URL
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setFormData(prev => ({ ...prev, referral_code: refCode.toUpperCase() }));
      setIsLogin(false); // Switch to register mode if there's a referral
    }
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Welcome back!');
      } else {
        await register(formData.email, formData.password, formData.name, formData.promoting, formData.referral_code);
        toast.success('Account created! Welcome to TrafficFlow AI!');
      }
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Something went wrong';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-void flex">
      {/* Left Panel - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 lg:px-16 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-12 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>
        
        <div className="max-w-md w-full mx-auto lg:mx-0">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-neon-mint rounded-xl flex items-center justify-center">
              <Zap className="w-7 h-7 text-black" />
            </div>
            <span className="font-heading font-bold text-2xl text-white">TrafficFlow</span>
            <span className="font-heading font-bold text-2xl text-neon-violet">AI</span>
          </div>
          
          {/* Referral Banner */}
          {!isLogin && formData.referral_code && (
            <div className="bg-neon-mint/10 border border-neon-mint/30 rounded-xl p-4 mb-6 flex items-center gap-3">
              <Gift className="w-5 h-5 text-neon-mint flex-shrink-0" />
              <div>
                <p className="text-neon-mint font-medium">Referral bonus active!</p>
                <p className="text-gray-400 text-sm">You'll get 150 credits (50 extra!)</p>
              </div>
            </div>
          )}
          
          <h1 className="font-heading font-bold text-3xl text-white mb-2">
            {isLogin ? 'Welcome back' : 'Create your account'}
          </h1>
          <p className="text-gray-400 mb-8">
            {isLogin 
              ? 'Sign in to continue earning and promoting' 
              : 'Start earning credits and growing your traffic'}
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-300">Full Name</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={handleChange}
                    required={!isLogin}
                    className="bg-void-subtle border-white/10 focus:border-neon-mint h-12 text-white placeholder:text-gray-600"
                    data-testid="name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="promoting" className="text-gray-300">What are you promoting?</Label>
                  <Input
                    id="promoting"
                    name="promoting"
                    type="text"
                    placeholder="My SaaS product, affiliate offers, etc."
                    value={formData.promoting}
                    onChange={handleChange}
                    className="bg-void-subtle border-white/10 focus:border-neon-mint h-12 text-white placeholder:text-gray-600"
                    data-testid="promoting-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="referral_code" className="text-gray-300">Referral Code (Optional)</Label>
                  <Input
                    id="referral_code"
                    name="referral_code"
                    type="text"
                    placeholder="Enter friend's code for bonus"
                    value={formData.referral_code}
                    onChange={handleChange}
                    className="bg-void-subtle border-white/10 focus:border-neon-mint h-12 text-white placeholder:text-gray-600 uppercase"
                    data-testid="referral-input"
                  />
                </div>
              </>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-300">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                required
                className="bg-void-subtle border-white/10 focus:border-neon-mint h-12 text-white placeholder:text-gray-600"
                data-testid="email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-300">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={6}
                className="bg-void-subtle border-white/10 focus:border-neon-mint h-12 text-white placeholder:text-gray-600"
                data-testid="password-input"
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full h-12 bg-neon-mint text-black hover:bg-neon-mint/90 font-bold shadow-neon btn-press"
              disabled={loading}
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : isLogin ? (
                'Sign In'
              ) : (
                'Create Account'
              )}
            </Button>
          </form>
          
          <p className="text-center text-gray-400 mt-6">
            {isLogin ? "Don't have an account?" : "Already have an account?"}{' '}
            <button 
              onClick={() => setIsLogin(!isLogin)}
              className="text-neon-mint hover:underline font-medium"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? 'Sign up' : 'Sign in'}
            </button>
          </p>
          
          {!isLogin && (
            <p className="text-center text-gray-500 text-sm mt-4">
              Get 50 free credits when you sign up!
            </p>
          )}
        </div>
      </div>
      
      {/* Right Panel - Image */}
      <div 
        className="hidden lg:block w-1/2 relative"
        style={{
          backgroundImage: `url(https://images.unsplash.com/photo-1764258057604-94e8e6ca092f?crop=entropy&cs=srgb&fm=jpg&q=85)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-void via-void/50 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <div className="bg-void/80 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
            <p className="text-white text-lg mb-4">
              "TrafficFlow helped me get 10x more eyes on my launch. The credit system is genius!"
            </p>
            <div className="flex items-center gap-3">
              <img 
                src="https://images.unsplash.com/photo-1740252117027-4275d3f84385?crop=entropy&cs=srgb&fm=jpg&q=85" 
                alt="User" 
                className="w-10 h-10 rounded-full object-cover"
              />
              <div>
                <p className="text-white font-medium">Alex Chen</p>
                <p className="text-gray-400 text-sm">Founder, StartupXYZ</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
